import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MixedText } from '../../utils/rtl';
import { analytics } from '../../services/analytics';
import { StudySession, studySessionsApi, coursesApi, aiApi, authApi } from '../../services/api';
import type { ChatMode } from '../../services/api';
import { Card, SpaceCard } from '../ui/Card';
import { Button } from '../ui/Button';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { ArrowLeft, Brain, Calendar, CheckCircle } from 'lucide-react';
import StudySessionLayout from './StudySessionLayout';
import ProgressTracker from './ProgressTracker';
import ChatWidget from './ChatWidget';
import UploadStep from './steps/UploadStep';
import MaterialsStep from './steps/MaterialsStep';
import QuestionsStep from './steps/QuestionsStep';
import CompleteStep from './steps/CompleteStep';

export type FlowStep = 'upload' | 'materials' | 'questions' | 'complete';

interface NewStudySessionProps {}

const NewStudySession: React.FC<NewStudySessionProps> = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const sessionId = parseInt(id || '0');

  // Core states
  const [session, setSession] = useState<StudySession | null>(null);
  const [courseSessions, setCourseSessions] = useState<StudySession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<FlowStep>('upload');

  // Chat states
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState<Array<{type: 'user' | 'ai', message: string}>>([]);
  const [messageFeedback, setMessageFeedback] = useState<{[messageIndex: number]: 'like' | 'dislike' | null}>({});
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [isChatMinimized, setIsChatMinimized] = useState(false);
  const [shouldUnminimizeChat, setShouldUnminimizeChat] = useState(false);
  const [chatMode, setChatMode] = useState<ChatMode>('fast');
  const [isVip, setIsVip] = useState(() => {
    try {
      const rawUser = localStorage.getItem('user');
      if (!rawUser) return false;
      return Boolean(JSON.parse(rawUser)?.is_vip);
    } catch {
      return false;
    }
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const userData = await authApi.getCurrentUser();
        if (!cancelled) {
          setIsVip(Boolean(userData?.is_vip));
        }
      } catch (err) {
        console.error('Failed to load user data for VIP status:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (isVip) {
      setChatMode('smart');
    }
  }, [isVip]);

  // Load session on mount
  useEffect(() => {
    if (sessionId) {
      loadSession();
      analytics.trackStudySessionViewed(sessionId);
    }
  }, [sessionId]);

  // Set default step based on session state
  useEffect(() => {
    if (session && currentStep === 'upload') {
      if (session.completed_at) {
        setCurrentStep('complete');
      } else if (hasQuestions()) {
        setCurrentStep('questions');
      } else if (hasStudyMaterials()) {
        setCurrentStep('materials');
      }
      // Removed auto-jump to materials when transcription completes
      // This allows users to upload multiple files without being forced to next step
    }
  }, [session]);

  // Helper functions
  const hasAudioUpload = () => Boolean(session?.transcriptions && session.transcriptions.length > 0);
  const hasDocuments = () => Boolean(session?.documents && session.documents.length > 0);
  const hasAnyContent = () => hasAudioUpload() || hasDocuments();
  const hasCompletedTranscription = () => Boolean(session?.transcriptions?.some(t => t.status === 'completed'));
  const hasStudyMaterials = () => Boolean(session?.study_materials && session.study_materials.length > 0);
  const hasQuestions = () => Boolean(session?.questions && session.questions.length > 0);

  // Load session data
  const loadSession = async () => {
    try {
      setLoading(true);
      const sessionData = await studySessionsApi.getSession(sessionId);
      setSession(sessionData);
      
      // Load course sessions for context
      if (sessionData.course_id) {
        try {
          const courseSessionsData = await coursesApi.getCourseSessions(sessionData.course_id);
          setCourseSessions(courseSessionsData);
        } catch (err) {
          console.error('Error loading course sessions:', err);
        }
      }
    } catch (err) {
      setError('Failed to load study session');
      console.error('Error loading session:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle session updates
  const updateSession = (updatedSession: StudySession) => {
    setSession(updatedSession);
  };

  // Handle step changes
  const handleStepChange = (step: FlowStep) => {
    setCurrentStep(step);
  };

  // Handle chat
  const handleChatToggle = async () => {
    if (!isChatOpen && sessionId) {
      // Opening chat - load history first
      try {
        const historyResponse = await aiApi.getChatHistory(sessionId);
        if (historyResponse.history && historyResponse.history.length > 0) {
          setChatHistory(historyResponse.history);
        }
        if (historyResponse.mode) {
          setChatMode(isVip ? 'smart' : (historyResponse.mode as ChatMode));
        }
      } catch (error) {
        console.error('Failed to load chat history:', error);
        // Don't throw error, just proceed with empty chat
      }
    }
    
    // If chat is already open and minimized, unminimize it
    if (isChatOpen && isChatMinimized) {
      setShouldUnminimizeChat(true);
      // Reset the flag after a brief delay
      setTimeout(() => setShouldUnminimizeChat(false), 100);
    } else {
      setIsChatOpen(!isChatOpen);
    }
  };

  const handleChatUpdate = (message: {type: 'user' | 'ai', message: string}) => {
    setChatHistory(prev => [...prev, message]);
  };

  const handleChatModeChange = useCallback(async (newMode: ChatMode) => {
    const previousMode = chatMode;
    setChatMode(newMode);
    if (!sessionId) return;
    try {
      const historyResponse = await aiApi.getChatHistory(sessionId, newMode);
      if (historyResponse.history && historyResponse.history.length > 0) {
        setChatHistory(historyResponse.history);
      } else {
        setChatHistory([]);
      }
      if (historyResponse.mode) {
        setChatMode(historyResponse.mode as ChatMode);
      }
    } catch (error) {
      console.error('Failed to switch chat mode:', error);
      setChatMode(previousMode);
      throw error;
    }
  }, [sessionId, chatMode]);

  // Handle message feedback
  const handleMessageFeedback = (messageIndex: number, feedback: 'like' | 'dislike') => {
    const aiMessage = chatHistory[messageIndex];
    if (aiMessage?.type !== 'ai') return;
    
    // Find the corresponding user message (previous message)
    const userMessage = messageIndex > 0 ? chatHistory[messageIndex - 1] : null;
    const userMessageContent = userMessage?.type === 'user' ? userMessage.message : '';
    
    // Update local state
    setMessageFeedback(prev => ({
      ...prev,
      [messageIndex]: prev[messageIndex] === feedback ? null : feedback
    }));
    
    // Track the feedback
    if (feedback === 'like') {
      analytics.trackChatMessageLiked(sessionId, messageIndex, aiMessage.message, userMessageContent);
    } else {
      analytics.trackChatMessageDisliked(sessionId, messageIndex, aiMessage.message, userMessageContent);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen py-8 flex items-center justify-center">
        <LoadingSpinner variant="astronaut" size="xl" className="w-48 h-48" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen py-8">
        <div className="text-center py-16">
          <h3 className="text-xl font-semibold text-surface-primary mb-2">
            {t('sessions:flow.errors.sessionNotFound')}
          </h3>
          <p className="text-surface-secondary mb-6">
            {t('sessions:flow.errors.sessionNotFoundDescription')}
          </p>
          <Button onClick={() => navigate('/')} variant="primary">
            {t('common:actions.back_to_dashboard')}
          </Button>
        </div>
      </div>
    );
  }

  const getSessionPosition = () => {
    if (!session || courseSessions.length === 0) return null;
    const sessionIndex = courseSessions.findIndex(s => s.id === session.id);
    return sessionIndex >= 0 ? { current: sessionIndex + 1, total: courseSessions.length } : null;
  };

  return (
    <StudySessionLayout
      session={session}
      courseSessions={courseSessions}
      getSessionPosition={getSessionPosition}
      isRTL={isRTL}
      onNavigateBack={() => navigate(`/course/${session.course_id}`)}
    >
      {/* Progress Tracker */}
      <ProgressTracker
        currentStep={currentStep}
        session={session}
        onStepChange={handleStepChange}
        isRTL={isRTL}
      />

      {/* Step Content */}
      <div className="min-h-[500px] space-y-8">
        {currentStep === 'upload' && (
          <UploadStep
            session={session}
            onSessionUpdate={updateSession}
            onStepChange={handleStepChange}
            isRTL={isRTL}
          />
        )}

        {currentStep === 'materials' && (
          <MaterialsStep
            session={session}
            onSessionUpdate={updateSession}
            onStepChange={handleStepChange}
            isRTL={isRTL}
          />
        )}

        {currentStep === 'questions' && (
          <QuestionsStep
            session={session}
            onSessionUpdate={updateSession}
            onStepChange={handleStepChange}
            isRTL={isRTL}
            onOpenChat={(message: string) => {
              // Set pending message and open chat
              setPendingMessage(message);
              if (!isChatOpen) {
                handleChatToggle();
              }
            }}
          />
        )}

        {currentStep === 'complete' && (
          <CompleteStep
            session={session}
            courseSessions={courseSessions}
            onSessionUpdate={updateSession}
            onStepChange={handleStepChange}
            isRTL={isRTL}
          />
        )}
      </div>

      {/* Floating Chat Widget */}
      <ChatWidget
        sessionId={sessionId}
        isOpen={isChatOpen}
        onClose={() => {
          setIsChatOpen(false);
          // Clear feedback state when chat is closed
          setMessageFeedback({});
          // Clear pending message when chat is closed
          setPendingMessage(null);
        }}
        chatHistory={chatHistory}
        onChatUpdate={handleChatUpdate}
        messageFeedback={messageFeedback}
        onMessageFeedback={handleMessageFeedback}
        isRTL={isRTL}
        pendingMessage={pendingMessage}
        onPendingMessageHandled={() => setPendingMessage(null)}
        onMinimizedStateChange={setIsChatMinimized}
        unminimizeChat={shouldUnminimizeChat}
        mode={isVip ? 'smart' : chatMode}
        onModeChange={isVip ? undefined : handleChatModeChange}
        enableModeSwitch={!isVip}
      />

      {/* Chat Toggle Button */}
      <button
        onClick={handleChatToggle}
        className="fixed bottom-4 md:bottom-6 right-4 md:right-6 z-[60] bg-gradient-to-r from-vibrant-orange to-vibrant-orange/90 hover:from-vibrant-orange/90 hover:to-vibrant-orange/80 text-white px-3 md:px-4 py-2.5 md:py-3 rounded-full shadow-lg shadow-vibrant-orange/30 transition-all duration-200 hover:scale-105 flex items-center gap-2 md:gap-3"
        title={t('sessions:flow.chat.openChat')}
      >
        <img
          src="/logo_v2.png"
          alt="StudyBel Astronaut"
          className="w-7 h-7 md:w-8 md:h-8 rounded-full bg-white/10 p-1"
        />
        <span className="font-medium text-xs md:text-sm whitespace-nowrap hidden sm:inline">
          <MixedText>{t('sessions:flow.chat.askQuestion', 'Ask Question')}</MixedText>
        </span>
      </button>
    </StudySessionLayout>
  );
};

export default NewStudySession; 
